import Linkify from 'linkify-react';
import type { ReactNode } from 'react';
import { Suspense, lazy, memo, useCallback, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Logo } from '@/components/Logo';
import MessageEditor from '@/components/MessageEditor';
import { LongPressMenu } from '@/components/UI/LongPressMenu';
import { useLongPress } from '@/renderer/utils/hooks/useLongPress';

// Lazy-load CreateRelease so non-admin users don't download the large form bundle
const CreateRelease = lazy(() => import('./CreateRelease'));
import FlagDropdown from './FlagDropdown';
import MessageEditMenu from './MessageEditMenu';
import NoteMenu from './NoteMenu';
import NotifyBadge from './NotifyBadge';

const Message = ({
  children,
  receipt,
  userInfo,
  isAdmin,
  showNotify,
  className: classes,
  updateRequest,
  precedingMessages,
  followingMessages,
}: {
  children?: ReactNode;
  receipt: API.MessageReceipt;
  userInfo?: boolean;
  isAdmin?: boolean;
  // Show the admin/staff-only notify badge (the notification type this message
  // went out as). Gated to staff+admin by the caller; members never get it.
  showNotify?: boolean;
  className?: string;
  updateRequest?: (...args: any[]) => void;
  // Feed messages just above this one (chronological), forwarded to the Create
  // Release form so it can resolve the units rating from a preceding directive
  // ("First play for 1.5 units" above the "Set up" line) (#1211).
  precedingMessages?: { content?: string; createdAt?: string }[];
  // Feed messages just BELOW this one (chronological), forwarded so the Create
  // Release form can auto-detect the GO / CANCEL / OPPOSITE follow-up that
  // resolves this "Set up" (the "Set Up & Go" flow). Updates live as the feed
  // grows, so the open form pre-selects the outcome when the follow-up lands.
  followingMessages?: { content?: string; createdAt?: string }[];
}) => {
  const time =
    !!receipt.message.createdAt &&
    Date.parse(receipt.message.createdAt) &&
    new Date(receipt.message.createdAt).toLocaleTimeString('en-us');

  const { message } = receipt;

  // Optimistic feedback for an admin hide: the soft-hide (visible: false) only
  // drops the message from the feed on next refresh, so grey it out immediately.
  const [hidden, setHidden] = useState(false);
  const onHidden = useCallback(() => setHidden(true), []);

  // Inline, edit-in-place editing (#1640): the admin toolbar's pencil flips this
  // message into a full-width editor that replaces the text below (like Slack),
  // instead of opening a modal. Save patches `content` via the same admin
  // endpoint; the message/updated broadcast then refreshes it for everyone.
  const [editing, setEditing] = useState(false);
  const onSaveEdit = useCallback(
    async (next: string) => {
      await (updateRequest as any)?.run(
        { data: { content: next } },
        {
          req: { input: `/api/v1/admin/message/update/${message.id}` },
          dontStoreError: true,
          dontAlterState: true,
        },
      );
    },
    [updateRequest, message.id],
  );

  // On touch there is no hover to reveal the admin toolbar. Rather than forcing
  // it always-on (`no-hover:*`), which pinned it permanently over the message
  // text on the compact width (#1553), a long-press opens the toolbar as an
  // anchored popover. Desktop keeps the hover/focus reveal untouched.
  const [toolbarPoint, setToolbarPoint] = useState<{ x: number; y: number } | null>(null);
  const hasMeta = !!receipt.message.meta?.flags?.length || !!receipt.message.meta?.note;
  const longPress = useLongPress((point) => {
    if (isAdmin) {
      // A long-press on iOS/WebKit can begin a native text selection before our
      // CSS suppression settles; clear any nascent range so the row doesn't stay
      // highlighted behind the toolbar popover (#2019).
      if (typeof window !== 'undefined') {
        window.getSelection?.()?.removeAllRanges();
      }
      setToolbarPoint(point);
    }
  });

  // Suppress the native iOS selection/callout on the pressed message content for
  // admins on touch. #2018 only guarded the row <article> and the popover card,
  // but the large selection starts on the message text itself, so the guard has
  // to sit on the content element too — including the explicit `-webkit-user-select`
  // that WebKit honors (Tailwind's `select-none` alone didn't stop it). Touch-only
  // (`no-hover:*`) so desktop keeps copy/select.
  const touchSelectGuard = isAdmin
    ? 'no-hover:select-none no-hover:[-webkit-user-select:none] no-hover:[-webkit-touch-callout:none]'
    : '';

  // The create/edit-release dialog is owned HERE, at the stable message row, not
  // inside the long-press action menu. The menu (desktop hover toolbar or the
  // touch LongPressMenu popover) only FIRES this open and then dismisses normally;
  // the dialog's lifecycle is independent, so closing the menu never tears the
  // dialog down. That ownership is the fix for #2006 — the previous attempt hosted
  // the dialog inside the menu's portal and had to keep a touch-capturing overlay
  // mounted to stop the menu from unmounting the dialog, which regressed the whole
  // native shell (#2008, reverted in #2017).
  const [releaseFormOpen, setReleaseFormOpen] = useState(false);
  const openReleaseForm = useCallback(() => {
    setToolbarPoint(null); // dismiss the long-press menu (if open) cleanly
    setReleaseFormOpen(true);
  }, []);

  const boldMessages = ['GO', 'CANCEL', 'OPPOSITE', 'Set up', 'Setup'];

  const messageContent = boldMessages.some((boldMessage) =>
    message.content.trim().startsWith(boldMessage),
  ) ? (
    <span className="font-bold">{message.content}</span>
  ) : (
    <>{message.content}</>
  );

  const adminToolbar = isAdmin ? (
    <>
      {/* Plain action button: it only opens the row-owned dialog (below) and, when
          fired from the touch popover, lets the menu dismiss normally. type="button"
          + onClick (no `form=`) so it works on iOS/WebKit. */}
      <button
        type="button"
        onClick={openReleaseForm}
        className="rounded-r-none hover:bg-emerald-500/5"
      >
        <div className="flex items-center space-x-2 p-2">
          <span className="font-semibold text-xs text-zinc-600 dark:text-zinc-400">
            {receipt.release ? 'Edit release' : 'Create release'}
          </span>
        </div>
      </button>
      <FlagDropdown
        id={receipt.message.id}
        flags={receipt.message.meta?.flags}
        updateRequest={updateRequest}
      />
      <NoteMenu
        id={receipt.message.id}
        note={receipt.message.meta?.note}
        updateRequest={updateRequest}
      />
      <MessageEditMenu
        id={receipt.message.id}
        content={receipt.message.content}
        updateRequest={updateRequest}
        onEdit={() => setEditing(true)}
        onHidden={onHidden}
      />
    </>
  ) : null;

  return (
    <article
      data-receiptid={receipt.id}
      {...(isAdmin ? longPress : {})}
      className={twMerge(
        'group relative flex w-full justify-start py-2 pr-4 align-top hover:bg-zinc-50 dark:hover:bg-white/5',
        hidden && 'opacity-50',
        // Touch (admin): suppress the native selection/callout during a
        // long-press and highlight the row while its toolbar popover is open.
        touchSelectGuard,
        toolbarPoint && 'bg-zinc-100 dark:bg-white/10',
        classes,
      )}
    >
      <div className="mx-2 flex w-14 shrink-0 flex-col items-center justify-start pt-1">
        {userInfo ? (
          <Logo className="size-8 select-none rounded-sm" />
        ) : (
          <time className="pt-px text-[9px] text-transparent group-hover:text-zinc-700 dark:group-hover:text-zinc-400">
            {time}
          </time>
        )}
      </div>
      <div className="w-full">
        {(userInfo || showNotify) && (
          // Admin/staff get the notify badge inline next to the byline (or on its
          // own for grouped messages). Kept left-aligned, clear of the hover admin
          // toolbar pinned to the message's top-right.
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {userInfo && (
              <span>
                <span className="font-bold text-sm dark:text-zinc-200">RAS</span>
                <time className="ml-2 text-xs text-zinc-500 dark:text-zinc-400">{time}</time>
              </span>
            )}
            {showNotify && (
              <>
                {hidden && (
                  <span className="inline-flex items-center whitespace-nowrap rounded-full bg-red-50 px-2 py-0.5 font-medium text-red-700 text-xs ring-1 ring-red-600/20 ring-inset dark:bg-red-500/10 dark:text-red-400 dark:ring-red-400/20">
                    Hidden
                  </span>
                )}
                <NotifyBadge notify={message.notify} />
              </>
            )}
          </div>
        )}
        {editing ? (
          <div className="pr-14">
            <MessageEditor
              content={message.content}
              onSave={onSaveEdit}
              onClose={() => setEditing(false)}
              saveLabel="Save changes"
            />
          </div>
        ) : (
          <div
            className={twMerge(
              'space-y-3 text-[15px] leading-[1.375rem] sm:text-sm/5 dark:text-zinc-300',
              // The message text is what actually starts the long-press selection,
              // so the guard lives here (and on the row) (#2019).
              touchSelectGuard,
            )}
          >
            <Linkify>
              <p className={twMerge('my-0 whitespace-pre-wrap break-words', touchSelectGuard)}>
                {messageContent || ''}
              </p>
            </Linkify>
          </div>
        )}
        {children}

        {isAdmin && (
          // Desktop only: reveal on hover/focus in the right gutter; a
          // flagged/noted message peeks at opacity-25. Hidden on touch
          // (`no-hover:hidden`), where a long-press opens the toolbar popover
          // below instead of pinning it over the message text (#1553).
          <div
            className={twMerge(
              '-mt-4 absolute top-0 right-14 hidden flex-row items-center divide-x rounded-sm bg-white opacity-25 ring-1 ring-zinc-900/10 no-hover:hidden focus-within:flex focus-within:opacity-100 group-hover:flex group-hover:opacity-100 dark:divide-zinc-700 dark:bg-zinc-900 dark:shadow-none dark:shadow-white/5 dark:ring-white/10',
              hasMeta && 'flex',
            )}
          >
            {adminToolbar}
          </div>
        )}
        {isAdmin && toolbarPoint && (
          <LongPressMenu point={toolbarPoint} onClose={() => setToolbarPoint(null)}>
            {/* `items-stretch` + centering each direct child gives every toolbar
                item the same height, so the `divide-x` separators run full-height
                and line up (the popover buttons mix a text label with icon-only
                controls of slightly different intrinsic heights) (#2019). */}
            <div className="flex items-stretch divide-x divide-zinc-900/10 dark:divide-zinc-700 [&>*]:flex [&>*]:items-center">
              {adminToolbar}
            </div>
          </LongPressMenu>
        )}
        {isAdmin && (
          // Row-owned create/edit dialog, controlled from this stable parent so it
          // is independent of the (desktop or touch) action menu that opens it
          // (#2006). Only its Modal mounts on open; when closed it costs the same
          // as the single always-mounted trigger it replaced.
          <Suspense fallback={null}>
            <CreateRelease
              receipt={receipt}
              release={receipt.release}
              precedingMessages={precedingMessages}
              followingMessages={followingMessages}
              isOpen={releaseFormOpen}
              onOpenChange={setReleaseFormOpen}
            />
          </Suspense>
        )}
      </div>
    </article>
  );
};

// A cheap fingerprint of the following-messages window (length + the last item),
// so the memo re-renders a "Set up" message when a GO / CANCEL / OPPOSITE
// follow-up lands (a new item grows the window) and the open Create Release form
// can auto-detect it. Non-set-up messages get no followingMessages, so this is ''
// for them and adds no re-renders.
const followingKey = (msgs?: { content?: string; createdAt?: string }[]) =>
  msgs && msgs.length
    ? `${msgs.length}|${msgs[msgs.length - 1]?.content ?? ''}|${msgs[msgs.length - 1]?.createdAt ?? ''}`
    : '';

// eslint-disable-next-line react-refresh/only-export-components
export default memo(
  Message,
  (prev, next) =>
    followingKey(prev.followingMessages) === followingKey(next.followingMessages) &&
    prev.showNotify === next.showNotify &&
    prev.receipt.message.content === next.receipt.message.content &&
    prev.receipt.message.notify === next.receipt.message.notify &&
    prev.receipt.message.visible === next.receipt.message.visible &&
    prev.receipt.updatedAt === next.receipt.updatedAt && // Receipt-level timestamp
    prev.receipt.release?.updatedAt === next.receipt.release?.updatedAt && // Release-level timestamp (if exists)
    // Compare the grade fields the <Release> badge actually renders, instead of
    // relying on release.updatedAt as a proxy for "the release changed". A live
    // grade arrives via the messageReceipt/updated refetch and is merged into the
    // list item; gating that badge's re-render on the rendered fields guarantees a
    // pending play flips to Win/Loss without a refresh (#1382).
    prev.receipt.release?.result === next.receipt.release?.result &&
    prev.receipt.release?.rating === next.receipt.release?.rating &&
    !!prev.receipt.message.meta === !!next.receipt.message.meta &&
    (!!prev.receipt.message.meta || !!next.receipt.message.meta
      ? JSON.stringify(prev.receipt.message.meta) === JSON.stringify(next.receipt.message.meta)
      : true),
);
